import { hashPrefixed, intlPrettyCurrency } from '../../../lib/utils'
import {
  EmbroiderySummaryCheckbox,
  EmbroiderySummarySneakPeek,
  EmbroiderySummaryView,
} from '../providers/facade'
import { EmbroideryFormKind, EmbroideryFormSubmissions, LogoFile } from '../types'

import {
  getConflictingEmbroideryType,
  getEmbroideryTypeForEmbroideryForm,
} from './embroideryFormUtils'
import { getEmbroideryPriceRange } from './getEmbroideryPriceRange'

import type { ProductCatalogEmbroideryTypes } from '../../../__generated__/graphql/catalog/graphql'

type EmbroideryPriceRangeByType = Partial<
  Record<ProductCatalogEmbroideryTypes, ReturnType<typeof getEmbroideryPriceRange>>
>

/**
 * Turns the chosen embroidery into everything the PDP summary card prints: the sneak peek for
 * each kind, the total, and the checkbox rows that let a shopper leave one out of the bag.
 * Everything is resolved ahead of time — prices formatted, labels translated, toggles bound —
 * so the card itself reads no state.
 */
export const deriveEmbroiderySummary = ({
  confirmAddEmbroidery,
  embroideryPriceRangeByType,
  embroiderySelections,
  isEmbroideryTypeSelectedForBag,
  locale,
  productColorHex,
  region,
  requiredEmbroideryTypes,
  savedEmbroideryPreferences,
  strings,
  toggleEmbroiderySelectionForAddToBag,
  transientLogo,
  withPricing,
}: {
  confirmAddEmbroidery: boolean
  embroideryPriceRangeByType: EmbroideryPriceRangeByType
  embroiderySelections: Partial<EmbroideryFormSubmissions>
  isEmbroideryTypeSelectedForBag: (embroideryType: ProductCatalogEmbroideryTypes) => boolean
  locale: Parameters<typeof intlPrettyCurrency>[0]['locale']
  productColorHex: string | undefined
  region: Parameters<typeof intlPrettyCurrency>[0]['region']
  requiredEmbroideryTypes: ProductCatalogEmbroideryTypes[]
  savedEmbroideryPreferences: object | null
  strings: {
    customize: string
    editEmbroidery: string
    embroideryItemLabel: string
    toggleAddEmbroidery: string
    toggleEmbroiderySelection: string
  }
  toggleEmbroiderySelectionForAddToBag: (formKind: EmbroideryFormKind) => void
  transientLogo: LogoFile | undefined
  withPricing: boolean
}): EmbroiderySummaryView => {
  const backgroundColor = hashPrefixed(productColorHex)
  const isPreferencePreview = !confirmAddEmbroidery && Boolean(savedEmbroideryPreferences)

  // De-duplicate form kinds that share one embroidery type, e.g. LOGO_SETUP and LOGO_DTC.
  const embroideryTypes = new Set(
    (Object.keys(embroiderySelections) as EmbroideryFormKind[])
      .filter(formKind => Boolean(embroiderySelections[formKind]))
      .map(formKind => getEmbroideryTypeForEmbroideryForm(formKind))
  )

  // Sum the prices for the embroidery types shown in the summary, regardless of checkbox state.
  const embroideryOptionPrice = Array.from(embroideryTypes).reduce((price, embroideryType) => {
    return price + (embroideryPriceRangeByType[embroideryType]?.effectivePrice.amount ?? 0)
  }, 0)

  // Show "digitization requested" for non-approved logos.
  const selectedLogoNeedsDigitization =
    Boolean(embroiderySelections.LOGO_DTC?.logoFile) &&
    embroiderySelections.LOGO_DTC?.logoFile?.status !== 'APPROVED'

  const getSummaryCheckbox = (
    formKind: EmbroideryFormKind
  ): EmbroiderySummaryCheckbox | undefined => {
    const embroideryType = getEmbroideryTypeForEmbroideryForm(formKind)
    const isRequired = requiredEmbroideryTypes.includes(embroideryType)
    if (isRequired || !embroiderySelections[formKind]) return undefined

    // Disable a checkbox when the mutually-exclusive embroidery type is already selected for bag.
    const conflictingType = getConflictingEmbroideryType(embroideryType)

    return {
      label: strings.toggleEmbroiderySelection,
      checked: isEmbroideryTypeSelectedForBag(embroideryType),
      disabledByConflict: conflictingType ? isEmbroideryTypeSelectedForBag(conflictingType) : false,
      onToggle: () => toggleEmbroiderySelectionForAddToBag(formKind),
    }
  }

  // One per kind, in a fixed order. A kind with nothing chosen is left off the card.
  const sneakPeeks: EmbroiderySummarySneakPeek[] = [
    {
      id: 'TEXT',
      kind: 'TEXT',
      checkbox: getSummaryCheckbox('TEXT'),
      props: {
        backgroundColor,
        textColorDisplayName: embroiderySelections.TEXT?.textColor?.displayName,
        textColorCssValue: embroiderySelections.TEXT?.textColor?.hexCode,
        textFirstLine: embroiderySelections.TEXT?.textFirstLine,
        textFontDisplayName: embroiderySelections.TEXT?.textFont?.displayName,
        textFontCssFamily: embroiderySelections.TEXT?.textFont?.designFile,
        textPlacement: embroiderySelections.TEXT?.textPlacement,
        textSecondLine: embroiderySelections.TEXT?.textSecondLine,
      },
    },
    {
      id: 'ICON',
      kind: 'ICON',
      checkbox: getSummaryCheckbox('ICON'),
      props: {
        backgroundColor,
        iconImageSource: embroiderySelections.ICON?.iconImageSource,
        iconInitials: embroiderySelections.ICON?.iconInitials,
        iconInitialsColorCss: embroiderySelections.ICON?.iconInitialsColor?.hexCode,
        iconInitialsColorDisplayName: embroiderySelections.ICON?.iconInitialsColor?.displayName,
        iconDesignName: embroiderySelections.ICON?.iconDesignName,
        iconPlacement: embroiderySelections.ICON?.iconPlacement,
      },
    },
    {
      id: 'LOGO_B2B',
      kind: 'LOGO_B2B',
      checkbox: getSummaryCheckbox('LOGO_B2B'),
      props: {
        backgroundColor,
        imgSrc: embroiderySelections.LOGO_B2B?.logoImageSource,
        logoName: embroiderySelections.LOGO_B2B?.logoName,
        logoPlacement: embroiderySelections.LOGO_B2B?.logoPlacement,
      },
    },
    {
      id: 'LOGO_DTC',
      kind: 'LOGO_DTC',
      checkbox: getSummaryCheckbox('LOGO_DTC'),
      props: {
        backgroundColor,
        imgSrc: embroiderySelections.LOGO_DTC?.logoPreview,
        logoName: embroiderySelections.LOGO_DTC?.logoName,
        logoPlacement: embroiderySelections.LOGO_DTC?.logoPlacement,
        logoColor: embroiderySelections.LOGO_DTC?.logoColor,
        logoNote: embroiderySelections.LOGO_DTC?.logoNote,
        hasLogoSetup:
          Boolean(embroiderySelections.LOGO_SETUP?.setupFile) ||
          (Boolean(transientLogo) &&
            transientLogo?.id === embroiderySelections.LOGO_DTC?.logoFile?.id) ||
          selectedLogoNeedsDigitization,
      },
    },
  ]

  return {
    editEmbroideryLabel: strings.editEmbroidery,
    editEmbroideryText: strings.customize,
    price: withPricing
      ? intlPrettyCurrency({ unit: 'dollars', value: embroideryOptionPrice, region, locale })
      : undefined,
    toggleAddEmbroideryText:
      isPreferencePreview || confirmAddEmbroidery
        ? strings.embroideryItemLabel
        : strings.toggleAddEmbroidery,
    sneakPeeks: sneakPeeks.filter(sneakPeek => Boolean(embroiderySelections[sneakPeek.kind])),
  }
}
